import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ExternalLink, Newspaper, Filter, X, Globe } from 'lucide-react';

import { API_BASE } from '@/utils/api';

const BACKEND_URL = API_BASE;

const REGIONS = [
  { id: 'all', name: 'All Regions' },
  { id: 'North America', name: 'North America' },
  { id: 'Europe', name: 'Europe' },
  { id: 'Asia', name: 'Asia' },
  { id: 'Middle East', name: 'Middle East' },
  { id: 'Africa', name: 'Africa' },
  { id: 'Oceania', name: 'Oceania' },
  { id: 'South America', name: 'South America' },
];

const REGION_COLORS = {
  'North America': '#ef4444',
  'Europe': '#3b82f6',
  'Asia': '#f59e0b',
  'Middle East': '#a855f7',
  'Africa': '#22c55e',
  'Oceania': '#06b6d4',
  'South America': '#ec4899',
};

// Persist failed URLs across re-renders so broken images never retry
const failedUrls = new Set();

const ArticleImage = React.memo(({ src }) => {
  const [visible, setVisible] = useState(true);

  if (!src || failedUrls.has(src)) return null;
  if (!visible) return null;

  const proxied = `${BACKEND_URL}/api/image-proxy?url=${encodeURIComponent(src)}`;
  return (
    <img
      src={proxied}
      alt=""
      className="ticker-article-image"
      onError={() => { failedUrls.add(src); setVisible(false); }}
    />
  );
});

export const NewsTicker = ({ moodColor }) => {
  const [articles, setArticles] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [showFilter, setShowFilter] = useState(false);
  const tickerRef = useRef(null);

  const fetchArticles = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mood/articles`);
      const data = await response.json();
      if (data.articles?.length) {
        setArticles(prev => {
          const newKeys = data.articles.map(a => a.link).join('|');
          const oldKeys = prev.map(a => a.link).join('|');
          return newKeys === oldKeys ? prev : data.articles;
        });
      }
    } catch (error) {
      // Keep existing articles on error
    }
  }, []);

  useEffect(() => {
    fetchArticles();
    const interval = setInterval(fetchArticles, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchArticles]);

  const filteredArticles = useMemo(() => {
    if (selectedRegion === 'all') return articles;
    return articles.filter(a => a.region === selectedRegion);
  }, [articles, selectedRegion]);

  const displayArticles = useMemo(
    () => [...filteredArticles, ...filteredArticles],
    [filteredArticles]
  );

  if (!articles.length) return null;

  return (
    <div className="news-ticker-container" data-testid="news-ticker">
      <div className="news-ticker-label">
        <Newspaper size={12} />
        <span>News</span>
      </div>

      <div className="news-ticker-filter-left">
        <button 
          className={`filter-btn ${showFilter ? 'active' : ''}`}
          onClick={() => setShowFilter(!showFilter)}
          title="Filter by region"
          data-testid="ticker-filter-btn"
        >
          <Filter size={12} />
        </button>
        
        {showFilter && (
          <div className="filter-dropdown">
            <div className="filter-header">
              <span>Filter by Region</span>
              <button onClick={() => setShowFilter(false)}><X size={14} /></button>
            </div>
            <div className="filter-options">
              {REGIONS.map(region => (
                <button
                  key={region.id}
                  className={`filter-option ${selectedRegion === region.id ? 'active' : ''}`}
                  onClick={() => { setSelectedRegion(region.id); setShowFilter(false); }}
                >
                  {region.name}
                  {selectedRegion === region.id && <span className="check">+</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div 
        className="news-ticker-track"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div 
          ref={tickerRef}
          className={`news-ticker-content ${isPaused ? 'paused' : ''}`}
          style={{ animationDuration: `${Math.max(60, filteredArticles.length * 4)}s` }}
        >
          {displayArticles.map((article, idx) => {
            const regionColor = REGION_COLORS[article.region] || moodColor || '#888';
            return (
              <a
                key={`${article.link}-${idx < filteredArticles.length ? 'a' : 'b'}`}
                href={article.link}
                target="_blank"
                rel="noopener noreferrer"
                className="news-ticker-item"
                style={{ '--ticker-color': regionColor }}
                title={article.title}
                data-testid={`ticker-article-${idx}`}
              >
                <ArticleImage src={article.image} />
                <div className="ticker-text">
                  <div className="ticker-source-line">
                    <span className="ticker-source" style={{ color: regionColor }}>
                      {article.source}
                    </span>
                    {article.region && (
                      <>
                        <span className="ticker-dot-sep">&#8226;</span>
                        <span className="ticker-region">{article.region}</span>
                      </>
                    )}
                  </div>
                  <span className="ticker-title">{article.title}</span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
      
      <button 
        className="news-ticker-region-btn"
        onClick={() => setShowFilter(!showFilter)}
        data-testid="ticker-region-btn"
      >
        <Globe size={12} />
        <span>{selectedRegion === 'all' ? 'All Regions' : selectedRegion}</span>
      </button>
    </div>
  );
};
